import React, { useMemo, useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import PropTypes from 'prop-types'
import { withTranslation } from 'react-i18next'

import { selectEstate as actionSE, usePageTitle } from '../../actions/app'
import { fetchConfigs as actionFC } from '../../actions/config'
import { fetchChannels as actionFCH } from '../../actions/content'
import { fetchSingleDevice as actionFSD, fetchStoreLocations as actionFSL } from '../../actions/device'
import { transferDevice as actionTD } from '../../actions/purgatory'
import { fetchStoresPaginated as actionFSP } from '../../actions/stores'
import { Button, Confirmation, Form, Headings, Input, List, Padding } from '../../components'
import { colors } from '../../constants'
import { SuccessMessage } from '../SetupDevice/SetupDevice.style'
import { filter } from 'lodash'

const { Label } = Form

const buildIdFromMac = (mac = '') => mac.replace(/:/g, '').toUpperCase()

const formatMac = (input = '') =>
    (
        input
            .toUpperCase() // Capitalize all letters
            .replace(/[^\d|A-F]/gi, '') // Remove all non hex characters
            .slice(0, 12) // Not more than 12 characters long
            .match(/.{1,2}/g) || []
    ) // Get characters grouped two by two
        .join(':') // Join all groups with colons

/**
 * Configure component
 * @param {Object} props
 */
const Configure = ({
    channels,
    config,
    estates,
    fetchChannels,
    fetchConfigs,
    fetchSingleDevice,
    fetchStoreLocations,
    fetchStoresPaginated,
    match: {
        params: { device: rawParamId },
    },
    selectedDevice,
    selectedEstate,
    selectEstate,
    storeLocations,
    stores,
    t,
    transferDevice,
}) => {
    usePageTitle(t('configure-device', { id: '' }))

    const paramId = typeof rawParamId === 'string' ? rawParamId.replace(/:/g, '') : rawParamId
    const [uid, setUid] = useState(paramId || '')
    const [mac, setMac] = useState(formatMac(paramId) || '')
    const [store, setStore] = useState(null)
    const [storeSearch, setStoreSearch] = useState('')
    const [storeLocation, setStoreLocation] = useState('1')
    const [sku, setSku] = useState('')
    const [channelId, setChannelId] = useState(null)
    const [channelSearch, setChannelSearch] = useState('')
    const [progress, setProgress] = useState(0)
    const [step, setStep] = useState('')
    const [error, setError] = useState(null)
    const [working, setWorking] = useState(false)
    const [rotation, setRotation] = useState('')

    const rotationOptions = [
        {value: "sensor", label:"Sensor"},
        {value: "portrait", label:"Portrait"},
        {value: "Landscape", label:"Landscape"}
    ]

    // estates formatted for the dropdown
    const estatesValues = useMemo(
        () =>
            estates
                .map(({ id: value, name }) => ({ value, name: `${value} ${name}` }))
                .sort(({ name: name1 = '' }, { name: name2 = '' }) => name1.localeCompare(name2)),
        [estates]
    )

    // stores formatted for the input
    const storesValues = useMemo(
        () =>
            (stores || []).map(({ code, name }) => ({
                key: code,
                value: `${code} ${name}`,
            })),
        [stores]
    )

    // store locations formatted for the dropdown
    const storeLocationsValues = useMemo(
        () =>
            (storeLocations || [])
                .map(({ id: value, name }) => ({ value, name }))
                .sort(({ name: name1 = '' }, { name: name2 = '' }) => name1.localeCompare(name2)),
        [storeLocations]
    )

    // store locations formatted for the dropdown
    const channelsValues = useMemo(
        () =>
            (channels || [])
                .map(({ id: key, name: value }) => ({ key, value }))
                .sort(({ name: name1 = '' }, { name: name2 = '' }) => name1.localeCompare(name2)),
        [channels]
    )

    const selectedChannel = useMemo(() => {
        if (typeof channelId !== 'number') {
            return null
        }

        return channels.find(({ id }) => id === channelId)
    }, [channelId, channels])

    /**
     * Generic on change for every input in the form
     * @param {Object} event
     */
    const onChangeInput = ({ target: { name, value } }) => {
        switch (name) {
            case 'mac':
                const partialMac = formatMac(value)
                setMac(partialMac)
                setUid(buildIdFromMac(partialMac))
                break
            case 'estate':
                selectEstate(value)
                break
            case 'store_code':
                setStore(stores.find(({ code }) => code === value))
                break
            case 'store_location':
                setStoreLocation(value)
                break
            case 'sku':
                setSku(value)
                break
            case 'channel':
                setChannelId(value)
                break
            case 'rotation':
                setRotation(value)
                break
            default:
                console.warn(name, value)
        }
    }

    

    /**
     * Get value from selected device or from config if device doesn't exist yet
     * @param {String} field Field to get data from
     */
    const getValue = (field) => (selectedDevice ? selectedDevice[field] : config[field])

    /**
     * Get selected store code, {undefined} if no store is selected
     */
    const getStoreCode = () => (store || {}).code

    /**
     * On submit form
     */
    const onSubmit = async () => {
        setError(null)
        setProgress(0)
        setStep('')
        setWorking(true)

        const { slug: channelSlug } = selectedChannel || {}

        try {
            const device = {
                api_url: config.api_url,
                apk_version: getValue('apk_version'),
                auth_key: getValue('auth_key'),
                background_colour: getValue('background_colour'),
                chain_id: getValue('chain_id'),
                device_owner: getValue('device_owner'),
                device_type: getValue('device_type'),
                disablezoom: getValue('disable_zoom'),
                antenna_level: getValue('antenna_level'),
                display_group: getValue('display_group'),
                file_auth: getValue('file_auth'),
                file_url: config.file_url,
                filter_level: getValue('filter_level') || '20',
                four_k: getValue('four_k'),
                id: uid,
                ip: '',
                layer_enabled: getValue('layer_enabled'),
                layer_extra: getValue('layer_extra'),
                mac,
                migrated_at: new Date().getTime(),
                ntp_url: getValue('ntp_url'),
                screen_rotation: getValue('screen_rotation'),
                selected_channel: (selectedChannel ? channelSlug : getValue('selected_channel')) || 'none',
                selected_type: getValue('selected_type'),
                sku,
                socket_url: config.socket_url,
                store_code: getStoreCode() || getValue('store_code'),
                store_location: storeLocation,
                store_sync: getValue('store_sync'),
                sync_delay: getValue('sync_delay'),
                sync_offset: getValue('sync_offset'),
                sync_timeout: getValue('sync_timeout'),
                sync_url: config.sync_url,
                volume: getValue('volume'),
                wifi_settings: getValue('wifi_settings'),
                working_close: getValue('working_close'),
                working_open: getValue('working_open'),
            }

            const onError = (stepError) => {
                setError(stepError)
            }

            const onProgress = (perc, progressStep) => {
                setProgress(perc)
                setStep(progressStep)
            }

            // Do the purgatory transfer
            await transferDevice(device, channelId, { onError, onProgress })
        } catch (err) {
            setError(err)
        } finally {
            setWorking(false)
        }
    }

    // When mac address in the url changes, set the new id
    useEffect(() => {
        setUid(paramId || '')
        setMac(formatMac(paramId) || '')
    }, [paramId])

    // When mac address in the url changes, set the new id
    useEffect(() => {
        if (uid.length === 12) {
            fetchSingleDevice(uid)
        }
    }, [uid, selectedEstate, fetchSingleDevice])

    // When selected device changes (device id changed on last render), load device details
    useEffect(() => {
        if (!selectedDevice) {
            return
        }

        setStore(selectedDevice.store)
        setStoreSearch('')
        setSku(selectedDevice.sku)

        if (selectedDevice.store_location) {
            setStoreLocation(selectedDevice.store_location?.id)
        }
    }, [selectedDevice])

    // When the store search phrase or the selected estate changes, fetch stores
    useEffect(() => {
        const fetchStores = (args = {}) => fetchStoresPaginated({ ...args, showEmptyStores: true })
        fetchStores({ search: storeSearch })
    }, [storeSearch, fetchStoresPaginated])

    // When the store search phrase or the selected estate changes, fetch stores
    useEffect(() => {
        fetchChannels(0, { name: channelSearch })
    }, [channelSearch, selectedEstate, fetchChannels])

    // When current estate changes, load estate store locations
    useEffect(() => {
        fetchConfigs()
        fetchStoreLocations()
        setStore(null)
        setStoreSearch('')
    }, [selectedEstate, fetchConfigs, fetchStoreLocations])

    return (
        <Padding>
            <div className='cofigure'>
            <Padding>
                <Headings.H2 color={colors.bluedark}>{t('configure-device', { id: uid })}</Headings.H2>
            </Padding>
            <Padding>
                <Label id="mac" label={t('mac-address')}>
                    <Input id="mac" name="mac" type="text" size="md" value={mac} onChange={onChangeInput} />
                </Label>
                <Label id="estate" label={t('estate')}>
                    <Input id="estate" name="estate" type="select" size="md" onChange={onChangeInput} value={selectedEstate} values={estatesValues} />
                </Label>
                <Label id="store_code" label={t('store-code')}>
                    {store && <List data={[store]} display="name" onRemove={() => setStore(null)} />}
                    {!store && (
                        <Input
                            type="suggestions"
                            onChange={(value) => onChangeInput({ target: { name: 'store_code', value } })}
                            onTextChange={(search) => {
                                setStoreSearch(search)
                            }}
                            options={storesValues}
                            size="md"
                        />
                    )}
                </Label>
                <Label id="store_location" label={t('store-location')}>
                    <Input
                        id="store_location"
                        name="store_location"
                        type="select"
                        size="md"
                        onChange={onChangeInput}
                        value={storeLocation}
                        values={storeLocationsValues}
                    />
                </Label>
                <Label label={t('sku')}>
                    <Input name="sku" type="text" size="md" value={sku} onChange={onChangeInput} />
                </Label>
                <Label label={t('channel')}>
                    {selectedChannel && <List data={[selectedChannel]} display="name" onRemove={() => setChannelId(null)} />}
                    {!selectedChannel && (
                        <Input
                            type="suggestions"
                            onChange={(value) => onChangeInput({ target: { name: 'channel', value } })}
                            onTextChange={(search) => {
                                setChannelSearch(search)
                            }}
                            options={channelsValues}
                            size="md"
                        />
                    )}
                </Label>
                <Label id="rotation" label={t('Rotation')}> 
                <Input
                id="rotation" name="rotation" type="select" size="md" onChange={onChangeInput} value={rotation} options={rotationOptions}/>
                </Label>
            </Padding>
            <Padding>
                <Padding bottom>
                    {error && <Headings.H2 color={colors.red}>{t(error, { context: 'error' })}</Headings.H2>}
                    {!error && t(step)}
                    {!error && progress === 1 && (
                        <SuccessMessage>The TV is now configured and you can close this page. The device is now downloading content.</SuccessMessage>
                    )}
                </Padding>
                <Confirmation
                    confirmation={t('configure-device-confirm')}
                    onConfirm={() => {
                        onSubmit()
                    }}
                >
                    <Button disabled={working} size="md" theme={working ? 'loading' : 'primary'}>
                        {working ? `${Number(progress * 100).toFixed(0)}%` : t('save')}
                    </Button>
                </Confirmation>
            </Padding>
            </div>
        </Padding>
    )
}

Configure.propTypes = {
    channels: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })).isRequired,
    config: PropTypes.shape({}).isRequired,
    estates: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    fetchChannels: PropTypes.func.isRequired,
    fetchConfigs: PropTypes.func.isRequired,
    fetchSingleDevice: PropTypes.func.isRequired,
    fetchStoreLocations: PropTypes.func.isRequired,
    fetchStoresPaginated: PropTypes.func.isRequired,
    match: PropTypes.shape({
        params: PropTypes.shape({
            mac: PropTypes.string,
        }).isRequired,
    }).isRequired,
    selectedDevice: PropTypes.shape({
        id: PropTypes.string,
        mac: PropTypes.string,
        store: PropTypes.shape({}),
        store_location: PropTypes.shape({
            id: PropTypes.string,
        }),
    }),
    selectedEstate: PropTypes.string.isRequired,
    selectEstate: PropTypes.func.isRequired,
    storeLocations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    stores: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    t: PropTypes.func.isRequired,
    transferDevice: PropTypes.func.isRequired,
}

Configure.defaultProps = {
    selectedDevice: null,
}

const mapStateToProps = (state) => {
    const {
        app: { estates, selectedEstate },
        configs: { configs },
        content: { channels },
        device: { selectedDevice, storeLocations },
        stores: { list: stores },
    } = state

    return {
        channels,
        config: (configs.find(({ key }) => key === 'DefaultUpdateForm') || {}).value || {},
        estates,
        selectedDevice,
        selectedEstate,
        storeLocations,
        stores,
    }
}

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            fetchConfigs: actionFC,
            fetchChannels: actionFCH,
            fetchSingleDevice: actionFSD,
            fetchStoreLocations: actionFSL,
            fetchStoresPaginated: actionFSP,
            selectEstate: actionSE,
            transferDevice: actionTD,
        },
        dispatch
    )

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(Configure))
